import { useEffect, useMemo, useState } from 'react'
import type { Category, QuoteSnapshot, Ticker } from '../types.ts'
import type { CategoryTreeNode, TickerWithValue } from '../hooks/useCategoriesTree.ts'
import { TickersRows } from './TickersRows.tsx'
import styles from './CategoriesTable.module.css'

type Props = {
  tree: CategoryTreeNode[]
  tickersByCategory: Map<number, TickerWithValue[]>
  quotesBySymbol: Record<string, QuoteSnapshot>
  deletingId?: number | null
  deletingCategoryId?: number | null
  onEditTicker: (ticker: Ticker) => void
  onDeleteTicker: (ticker: Ticker) => void
  onEditCategory: (category: Category) => void
  onDeleteCategory: (category: Category) => void
}

function formatCurrency(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function collectInitiallyExpandedIds(
  nodes: CategoryTreeNode[],
  tickersByCategory: Map<number, TickerWithValue[]>,
) {
  const expandedIds = new Set<number>()

  const visit = (currentNodes: CategoryTreeNode[]) => {
    for (const node of currentNodes) {
      const hasChildren = node.children.length > 0 || (tickersByCategory.get(node.id)?.length ?? 0) > 0

      if (hasChildren) {
        expandedIds.add(node.id)
      }

      if (node.children.length > 0) {
        visit(node.children)
      }
    }
  }

  visit(nodes)

  return expandedIds
}

function Tree({
  nodes,
  tickersByCategory,
  quotesBySymbol,
  expandedIds,
  onToggle,
  deletingId,
  deletingCategoryId,
  onEditTicker,
  onDeleteTicker,
  onEditCategory,
  onDeleteCategory,
  level = 0,
}: {
  nodes: CategoryTreeNode[]
  tickersByCategory: Map<number, TickerWithValue[]>
  quotesBySymbol: Record<string, QuoteSnapshot>
  expandedIds: Set<number>
  onToggle: (categoryId: number) => void
  deletingId?: number | null
  deletingCategoryId?: number | null
  onEditTicker: (ticker: Ticker) => void
  onDeleteTicker: (ticker: Ticker) => void
  onEditCategory: (category: Category) => void
  onDeleteCategory: (category: Category) => void
  level?: number
}) {
  return (
    <ul className={level === 0 ? styles.tree : styles.treeNested}>
      {nodes.map((node) => {
        const levelClass = styles[`level${Math.min(level, 5)}`]
        // Cada categoria pode ter dois tipos de filhos: subcategorias e tickers.
        const categoryTickers = tickersByCategory.get(node.id) ?? []
        const hasChildren = node.children.length > 0 || categoryTickers.length > 0
        const isExpanded = expandedIds.has(node.id)

        return (
          <li key={node.id} className={styles.item}>
            <div className={`${styles.categoryHeader} ${levelClass}`}>
              <div className={styles.categoryMain}>
                {hasChildren ? (
                  <button
                    type="button"
                    className={styles.categoryToggle}
                    onClick={() => onToggle(node.id)}
                    aria-label={isExpanded ? `Recolher ${node.name}` : `Expandir ${node.name}`}
                  >
                    <span className={styles.categoryTogglePrefix} aria-hidden="true">
                      {isExpanded ? '➖' : '➕'}
                    </span>
                    <span className={level === 0 ? styles.categoryNameRoot : styles.categoryName}>
                      {node.name}
                    </span>
                  </button>
                ) : (
                  <div className={level === 0 ? styles.categoryNameRoot : styles.categoryName}>
                    {node.name}
                  </div>
                )}

                {/* Novo total derivado da categoria e de toda a sua subárvore. */}
                <span className={styles.categoryTotal}>{formatCurrency(node.totalValue)}</span>
              </div>

              {!node.isRoot ? (
                <div className={styles.categoryActions}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => onEditCategory(node)}
                    title="Editar categoria"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => onDeleteCategory(node)}
                    disabled={deletingCategoryId === node.id}
                    title="Deletar categoria"
                  >
                    {deletingCategoryId === node.id ? '…' : '✕'}
                  </button>
                </div>
              ) : null}
            </div>

            {/* Quando recolhido, escondemos tanto subcategorias quanto tickers desse nó. */}
            {isExpanded && node.children.length > 0 ? (
              <Tree
                nodes={node.children}
                tickersByCategory={tickersByCategory}
                quotesBySymbol={quotesBySymbol}
                expandedIds={expandedIds}
                onToggle={onToggle}
                deletingId={deletingId}
                deletingCategoryId={deletingCategoryId}
                onEditTicker={onEditTicker}
                onDeleteTicker={onDeleteTicker}
                onEditCategory={onEditCategory}
                onDeleteCategory={onDeleteCategory}
                level={level + 1}
              />
            ) : null}

            {isExpanded && categoryTickers.length > 0 ? (
              <TickersRows
                tickers={categoryTickers}
                quotesBySymbol={quotesBySymbol}
                deletingId={deletingId}
                onEdit={onEditTicker}
                onDelete={onDeleteTicker}
              />
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

export function CategoriesTable({
  tree,
  tickersByCategory,
  quotesBySymbol,
  deletingId,
  deletingCategoryId,
  onEditTicker,
  onDeleteTicker,
  onEditCategory,
  onDeleteCategory,
}: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const initiallyExpandedIds = useMemo(
    () => collectInitiallyExpandedIds(tree, tickersByCategory),
    [tree, tickersByCategory],
  )

  useEffect(() => {
    setExpandedIds(initiallyExpandedIds)
  }, [initiallyExpandedIds])

  // Toggle local e imutável para manter a árvore responsiva sem alterar os dados de origem.
  function handleToggle(categoryId: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev)

      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }

      return next
    })
  }

  return (<>
      <Tree
        nodes={tree}
        tickersByCategory={tickersByCategory}
        quotesBySymbol={quotesBySymbol}
        expandedIds={expandedIds}
        onToggle={handleToggle}
        deletingId={deletingId}
        deletingCategoryId={deletingCategoryId}
        onEditTicker={onEditTicker}
        onDeleteTicker={onDeleteTicker}
        onEditCategory={onEditCategory}
        onDeleteCategory={onDeleteCategory}
      />
  </>)
}
